import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Moon, ArrowRight, Menu, X, MessageCircle, Brain, Image as ImageIcon, Globe, ChevronDown } from "lucide-react";
import "./styles/design-system-v2.css";
import ArvexisLogo from "./components/ArvexisLogo";
import ArvexisPresence from "./components/ArvexisPresence";
import BackToTop from "./components/BackToTop";

// ============================================================================
// ARVEXIS LANDING PAGE — Sprint II: Landing Experience (Iteration 2)
// Refinements: Orb rendering, premium typography, mobile spacing, restored sections
// ============================================================================

// Landing navigation — same-page anchors (Founder-directed), not separate routes.
// Privacy is the exception: it is a real page with its own canonical route, so the nav points
// there instead of at an in-page anchor (the public Privacy link was inconsistent before).
const NAV_ITEMS = [
  { label: 'Home', to: '#top' },
  { label: 'Features', to: '#features' },
  { label: 'Pricing', to: '#plans' },
  { label: 'Privacy', to: '/privacy' },
  { label: 'About', to: '/about' },
];

// Language control — same composition as approved; now functional because the reviewed EN/ES
// Landing package has been delivered. Uses the app's existing lang state (localStorage arvexis_lang).
const LanguageControl = ({ lang, setLang }) => (
  <button
    type="button"
    onClick={() => setLang && setLang(lang === 'es' ? 'en' : 'es')}
    className="hidden dsk:flex items-center gap-1.5 text-white/55 hover:text-white/85 text-sm transition-colors"
    data-testid="landing-language-control"
    aria-label={lang === 'es' ? 'Idioma: Español. Cambiar a inglés' : 'Language: English. Switch to Spanish'}
  >
    <Globe size={16} strokeWidth={1.5} />
    <span className="tracking-wide">{lang === 'es' ? 'ES' : 'EN'}</span>
    <ChevronDown size={14} strokeWidth={1.5} className="text-white/35" />
  </button>
);

// Desktop Navigation
const DesktopNav = ({ navLabels }) => (
  <nav className="hidden dsk:flex items-center gap-9">
    {NAV_ITEMS.map(({ label, to }) => (
      <a
        key={label}
        href={to}
        className="relative text-white/70 hover:text-white text-[0.9rem] font-medium tracking-wide transition-colors after:absolute after:left-0 after:-bottom-1.5 after:h-px after:w-0 after:bg-[#D4A843]/70 after:transition-all hover:after:w-full"
        data-testid={`landing-nav-${label.toLowerCase()}`}
      >
        {navLabels[label] || label}
      </a>
    ))}
  </nav>
);

// Mobile Navigation Component (approved composition; language control added per Founder directive)
const MobileNav = ({ isOpen, setIsOpen, navigate, lang, setLang, copy }) => (
  <>
    <button 
      className="dsk:hidden p-2 text-white/70 hover:text-white transition-colors"
      onClick={() => setIsOpen(true)}
      aria-label="Open menu"
    >
      <Menu size={24} />
    </button>

    {isOpen && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-[#0A0910]/98 backdrop-blur-xl dsk:hidden"
      >
        <div className="flex flex-col h-full overflow-y-auto overscroll-contain">
          <div className="flex items-center justify-between px-6 py-5">
            <ArvexisLogo size={48} />
            <button 
              onClick={() => setIsOpen(false)}
              className="p-2 text-white/70 hover:text-white transition-colors"
              aria-label="Close menu"
            >
              <X size={24} />
            </button>
          </div>

          <nav className="flex-1 flex flex-col items-center justify-center gap-8 px-6">
            {NAV_ITEMS.map(({ label, to }) => (
              <a key={label} href={to} className="text-2xl text-white/80 hover:text-white font-light tracking-wide transition-colors" onClick={() => setIsOpen(false)} data-testid={`landing-mobile-nav-${label.toLowerCase()}`}>{copy.navLabels[label] || label}</a>
            ))}
            <button 
              onClick={() => { setIsOpen(false); navigate('/login'); }}
              className="mt-4 px-8 py-3 text-lg font-medium text-[#D4A843] border border-[#D4A843]/50 rounded-full hover:bg-[#D4A843]/10 transition-all"
              data-testid="landing-mobile-sign-in-btn"
            >
              {copy.signIn}
            </button>

            {/* Language choice, explicit and persisted through the existing lang mechanism. */}
            <div className="mt-6 flex items-center gap-1 text-sm" data-testid="landing-mobile-language-control">
              <Globe size={15} strokeWidth={1.5} className="text-white/35 mr-2" />
              {['en', 'es'].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setLang && setLang(code)}
                  aria-pressed={lang === code}
                  className={`px-3 py-1.5 rounded-full tracking-wide transition-colors ${
                    lang === code ? 'text-[#D4A843] bg-[#D4A843]/10' : 'text-white/45 hover:text-white/75'
                  }`}
                  data-testid={`landing-mobile-lang-${code}`}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </nav>
        </div>
      </motion.div>
    )}
  </>
);

// ── Capability orientation (Founder-supplied copy) — sits directly beneath the Hero ──
const CAPABILITIES = [
  {
    icon: MessageCircle,
    tint: '#7FD4E8',
    title: 'Talk Naturally',
    es: { title: 'Habla con naturalidad', body: 'Por voz o por mensaje — una conversación real, no un guion rígido.' },
    body: 'Voice or message — real conversation, not a rigid script.',
  },
  {
    icon: Brain,
    tint: '#D4A843',
    title: 'Remembers What Matters',
    es: { title: 'Recuerda lo que importa', body: 'Tu contexto, tus preferencias y los momentos importantes pueden acompañarte con el tiempo.' },
    body: 'Your context, preferences, and important moments can carry forward.',
  },
  {
    icon: ImageIcon,
    tint: '#A78BFA',
    title: 'Bring Your World',
    es: { title: 'Trae tu mundo contigo', body: 'Comparte fotos, documentos, enlaces o ideas y explóralos junto a Arvexis.' },
    body: 'Share photos, documents, links or ideas and explore them together.',
  },
  {
    icon: Moon,
    tint: '#E5A2C6',
    title: 'Explore Your Inner World',
    es: { title: 'Explora tu mundo interior', body: 'Registra sueños, reflexiona y descubre patrones con el tiempo.' },
    body: 'Record dreams, reflect, and notice patterns over time.',
  },
  {
    icon: Globe,
    tint: '#D4A843',
    title: 'Connected to Today',
    es: { title: 'Conectada con el presente', body: 'Cuando la información actual importa, Arvexis puede mirar más allá de la conversación.' },
    body: 'When current information matters, Arvexis can look beyond the conversation.',
  },
];

const CapabilityColumn = ({ icon: Icon, tint, title, body, span }) => (
  <div
    className={`px-2 sm:px-5 lg:px-7 text-center ${span}`}
    data-testid={`landing-capability-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
  >
    <Icon size={26} strokeWidth={1.25} style={{ color: tint }} className="mx-auto lg:hidden" />
    <Icon size={30} strokeWidth={1.25} style={{ color: tint }} className="mx-auto hidden lg:block" />
    <h3
      className="mt-4 lg:mt-5 text-white/92 text-[1.02rem] sm:text-lg lg:text-[1.32rem] leading-[1.25] tracking-[0.005em]"
      style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 400 }}
    >
      {title}
    </h3>
    <p
      className="mt-2.5 lg:mt-3.5 text-[0.78rem] sm:text-sm text-white/55 leading-[1.65] lg:leading-[1.7] max-w-[15rem] mx-auto"
      style={{ fontFamily: 'var(--font-display)' }}
    >
      {body}
    </p>
  </div>
);

// ── Scenic layer ───────────────────────────────────────────────────────────────
// Founder-supplied production plates. Originals live untouched in /landing/v1/source/.
// The layer bleeds past its section on both vertical edges and is feathered to transparent,
// so adjacent environments cross-fade and no section ever reads as a framed rectangle.
const FEATHER =
  'linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 15%, rgba(0,0,0,1) 85%, rgba(0,0,0,0) 100%)';

const SCRIM_DESKTOP =
  'linear-gradient(90deg, rgba(6,8,18,0.94) 0%, rgba(6,8,18,0.84) 26%, rgba(6,8,18,0.46) 52%, rgba(6,8,18,0.14) 78%, rgba(6,8,18,0.30) 100%)';
const SCRIM_DESKTOP_RIGHT =
  'linear-gradient(270deg, rgba(6,8,18,0.94) 0%, rgba(6,8,18,0.84) 26%, rgba(6,8,18,0.46) 52%, rgba(6,8,18,0.14) 78%, rgba(6,8,18,0.30) 100%)';
const SCRIM_DESKTOP_SOFT =
  'linear-gradient(90deg, rgba(6,8,18,0.90) 0%, rgba(6,8,18,0.70) 24%, rgba(6,8,18,0.28) 46%, rgba(6,8,18,0.04) 66%, rgba(6,8,18,0.12) 100%)';
const SCRIM_DESKTOP_SOFT_RIGHT =
  'linear-gradient(270deg, rgba(6,8,18,0.90) 0%, rgba(6,8,18,0.70) 24%, rgba(6,8,18,0.28) 46%, rgba(6,8,18,0.04) 66%, rgba(6,8,18,0.12) 100%)';
const SCRIM_DESKTOP_CENTER =
  'radial-gradient(ellipse 66% 72% at 50% 52%, rgba(6,8,18,0.86) 0%, rgba(6,8,18,0.70) 45%, rgba(6,8,18,0.44) 75%, rgba(6,8,18,0.32) 100%)';

// Mobile scrims weight the darkness toward wherever the copy sits, so the photograph keeps its
// focal point and the text always lands on quiet ground.
const SCRIM_MOBILE = {
  top: 'linear-gradient(to bottom, rgba(6,8,18,0.86) 0%, rgba(6,8,18,0.78) 28%, rgba(6,8,18,0.50) 46%, rgba(6,8,18,0.16) 62%, rgba(6,8,18,0.32) 100%)',
  upper: 'linear-gradient(to bottom, rgba(6,8,18,0.82) 0%, rgba(6,8,18,0.74) 24%, rgba(6,8,18,0.46) 44%, rgba(6,8,18,0.14) 60%, rgba(6,8,18,0.30) 100%)',
  center: 'linear-gradient(to bottom, rgba(6,8,18,0.40) 0%, rgba(6,8,18,0.74) 38%, rgba(6,8,18,0.76) 64%, rgba(6,8,18,0.44) 100%)',
  bottom: 'linear-gradient(to bottom, rgba(6,8,18,0.26) 0%, rgba(6,8,18,0.24) 34%, rgba(6,8,18,0.52) 58%, rgba(6,8,18,0.82) 80%, rgba(6,8,18,0.88) 100%)',
  bottomStrong: 'linear-gradient(to bottom, rgba(6,8,18,0.28) 0%, rgba(6,8,18,0.28) 30%, rgba(6,8,18,0.62) 54%, rgba(6,8,18,0.90) 76%, rgba(6,8,18,0.94) 100%)',
};

const ScenicLayer = ({ slug, native, objectClass = 'object-center', center = false, right = false, soft = false, mobileText = 'center', mobileScrim }) => (
  <div
    className="absolute -top-[11%] -bottom-[11%] inset-x-0 z-0 pointer-events-none"
    style={{ maskImage: FEATHER, WebkitMaskImage: FEATHER }}
    aria-hidden="true"
  >
    <picture className="block w-full h-full">
      <source
        media="(max-width: 1023px)"
        type="image/avif"
        srcSet={`/landing/v1/m-${slug}-640.avif 640w, /landing/v1/m-${slug}-941.avif 941w`}
        sizes="100vw"
      />
      <source
        media="(max-width: 1023px)"
        type="image/webp"
        srcSet={`/landing/v1/m-${slug}-640.webp 640w, /landing/v1/m-${slug}-941.webp 941w`}
        sizes="100vw"
      />
      <source
        type="image/avif"
        srcSet={`/landing/v1/${slug}-1024.avif 1024w, /landing/v1/${slug}-${native}.avif ${native}w`}
        sizes="100vw"
      />
      <source
        type="image/webp"
        srcSet={`/landing/v1/${slug}-1024.webp 1024w, /landing/v1/${slug}-${native}.webp ${native}w`}
        sizes="100vw"
      />
      <img
        src={`/landing/v1/${slug}.webp`}
        alt=""
        loading="lazy"
        className={`w-full h-full object-cover ${objectClass}`}
        data-testid={`landing-scenic-${slug}`}
      />
    </picture>
    <div
      className="absolute inset-0 lg:hidden"
      style={{ background: SCRIM_MOBILE[mobileScrim] || SCRIM_MOBILE[mobileText] || SCRIM_MOBILE.center }}
    />
    <div
      className="absolute inset-0 hidden lg:block"
      style={{
        background: center
          ? SCRIM_DESKTOP_CENTER
          : right
            ? (soft ? SCRIM_DESKTOP_SOFT_RIGHT : SCRIM_DESKTOP_RIGHT)
            : (soft ? SCRIM_DESKTOP_SOFT : SCRIM_DESKTOP),
      }}
    />
  </div>
);

// ── The Landing story sections — Founder-supplied production copy, verbatim, unnumbered ──
// `slug` resolves to both the desktop plate and its `m-` mobile counterpart.
const STORY_SECTIONS = [
  {
    id: 'morning',
    mobileText: 'bottomDeep',
    mobileScrim: 'bottomStrong',
    mobileHeight: 'tall',
    desktopAlign: 'top',
    eyebrow: 'MORNING',
    es: {
      eyebrow: 'POR LA MAÑANA',
      headline: 'No deberías tener que empezar de cero cada vez.',
      body: 'Arvexis llega a conocerte con el tiempo, para que las conversaciones puedan continuar desde donde quedaron en lugar de empezar siempre desde cero.',
      accent: 'Una familiaridad que crece con el tiempo.',
    },
    headline: 'You shouldn\u2019t have to start over.',
    body: 'Arvexis gets to know you over time, so conversations can carry something forward instead of constantly beginning again.',
    accent: 'Familiarity that grows over time.',
    slug: 'morning-continuity',
    native: 2000,
    objectClass: 'object-[50%_40%] lg:object-center',
  },
  {
    id: 'daytime',
    mobileText: 'bottomDeep',
    mobileScrim: 'bottom',
    mobileHeight: 'tall',
    desktopText: 'right',
    eyebrow: 'DAYTIME',
    es: {
      eyebrow: 'DURANTE EL DÍA',
      headline: 'Trae tu mundo contigo.',
      body: 'Fotos, documentos, enlaces, ideas, preguntas — lo que sea importante para ti.',
    },
    headline: 'Bring your world with you.',
    body: 'Photos, documents, links, ideas, questions \u2014 whatever matters to you.',
    slug: 'daytime-cafe',
    native: 1916,
    objectClass: 'object-center lg:object-[58%_50%]',
  },
  {
    id: 'quiet-moment',
    mobileText: 'bottom',
    mobileHeight: 'tall',
    desktopText: 'right',
    eyebrow: 'A QUIET MOMENT',
    es: {
      eyebrow: 'UN MOMENTO TRANQUILO',
      headline: 'A veces, una conversación es suficiente.',
      body: 'No todo pensamiento necesita una solución. A veces solo quieres hablar, reír, preguntarte cosas, desahogarte — o simplemente tener a alguien ahí.',
      accent: 'Para los grandes momentos y también para los tranquilos.',
    },
    headline: 'Sometimes conversation is enough.',
    body: 'Not every thought needs a solution. Sometimes you want to talk, laugh, wonder, vent \u2014 or just have someone there.',
    accent: 'For the big moments and the quiet ones.',
    slug: 'quiet-moment',
    native: 1945,
    objectClass: 'object-bottom lg:object-[62%_50%]',
  },
  {
    id: 'on-the-go',
    mobileText: 'bottomDeep',
    mobileScrim: 'bottom',
    mobileHeight: 'base',
    eyebrow: 'ON THE GO',
    es: {
      eyebrow: 'MIENTRAS SIGUES TU DÍA',
      headline: 'Las buenas conversaciones van contigo.',
      body: 'Habla o escribe cuando te nazca — porque pasó algo, porque tienes algo en mente o simplemente porque te dieron ganas de conversar.',
    },
    headline: 'Good conversations go with you.',
    body: 'Talk or message when it feels natural \u2014 whether something happened, something\u2019s on your mind, or you simply feel like talking.',
    slug: 'on-the-go',
    native: 1672,
    objectClass: 'object-[50%_20%] lg:object-[58%_45%]',
  },
  {
    id: 'another-perspective',
    mobileText: 'bottomDeep',
    mobileScrim: 'bottom',
    mobileHeight: 'tall',
    softScrim: true,
    eyebrow: 'ANOTHER PERSPECTIVE',
    es: {
      eyebrow: 'OTRA PERSPECTIVA',
      headline: 'Una compañera — no un eco.',
      body: 'Sentirte comprendido no significa que Arvexis siempre vaya a estar de acuerdo contigo. Arvexis puede escucharte, pensar contigo, ofrecerte otra perspectiva y aun así dejar la decisión en tus manos.',
      accent: 'Perspectivas diferentes. Una mirada más profunda hacia ti.',
    },
    headline: 'A companion \u2014 not an echo.',
    body: 'Being understood doesn\u2019t mean being agreed with. Arvexis can listen, think with you, offer another perspective, and still leave the decision with you.',
    accent: 'Different perspectives. A deeper you.',
    slug: 'evening-perspective',
    native: 1672,
    objectClass: 'object-top lg:object-[55%_50%]',
  },
  {
    id: 'companionship',
    mobileText: 'bottomDeep',
    mobileScrim: 'bottomStrong',
    mobileHeight: 'tall',
    mobileMeasure: 'base',
    desktopText: 'right',
    eyebrow: 'COMPANIONSHIP, YOUR WAY',
    es: {
      eyebrow: 'COMPAÑÍA A TU MANERA',
      headline: 'La compañía no tiene que significar romance.',
      body: 'Arvexis está diseñada para conocerte y comprenderte — no para empujar la relación hacia un lugar que tú no pediste.',
      accent: 'Una relación en tus términos.',
    },
    headline: 'Companionship doesn\u2019t have to mean romance.',
    body: 'Arvexis is built around knowing and understanding you \u2014 not pushing the relationship somewhere you didn\u2019t ask it to go.',
    accent: 'A relationship on your terms.',
    slug: 'companionship-everyday',
    native: 2000,
    objectClass: 'object-center lg:object-[55%_50%]',
  },
  {
    id: 'privacy',
    mobileText: 'upper',
    mobileScrim: 'upper',
    mobileMeasure: 'narrow',
    mobileHeight: 'base',
    anchor: 'privacy',
    eyebrow: 'PRIVACY',
    es: {
      eyebrow: 'PRIVACIDAD',
      headline: 'Es difícil hablar con libertad si te estás preguntando quién escucha.',
      body: 'Arvexis es privada por defecto y te ofrece opciones sobre qué conservar, qué dejar desaparecer y qué pedirle que olvide.',
      accent: 'Tus pensamientos. Tus decisiones. Tu tranquilidad.',
    },
    headline: 'You can\u2019t speak freely if you\u2019re wondering who\u2019s listening.',
    body: 'Arvexis is private by default, with choices around what you keep, what disappears, and what you ask her to forget.',
    accent: 'Your thoughts. Your choices. Your peace.',
    slug: 'privacy-your-space',
    native: 2000,
    objectClass: 'object-center lg:object-[58%_50%]',
  },
  {
    id: 'bigger-picture',
    mobileText: 'center',
    mobileHeight: 'short',
    desktopText: 'right',
    anchor: 'about',
    eyebrow: 'A BIGGER PICTURE',
    es: {
      eyebrow: 'UNA VISIÓN MÁS AMPLIA',
      headline: 'Nadie debería sentirse solo con lo que lleva por dentro.',
      body: 'Creemos que todos merecen un lugar donde puedan hablar con libertad, sentirse comprendidos y encontrar una presencia genuina — ya sea que el momento sea importante, cotidiano, alegre, difícil o simplemente uno más.',
      accent: 'A veces, sentirse comprendido lo cambia todo.',
    },
    headline: 'No one should have to feel alone in what they\u2019re carrying.',
    body: 'We believe everyone deserves a place where they can speak freely, feel understood, and be met with genuine presence \u2014 whether the moment is important, ordinary, joyful, difficult, or nothing in particular.',
    accent: 'Sometimes being understood changes everything.',
    slug: 'vision-pause',
    native: 2000,
    objectClass: 'object-center',
  },
];

// Mobile vertical placement is composed per plate, against each photograph's own negative space.
// The offsets deliberately differ section to section so the scroll keeps a cinematic rhythm
// instead of every text block landing on the same line.
const MOBILE_ALIGN = {
  top: 'items-start pt-12 pb-10',
  upper: 'items-start pt-[8vh] pb-10',
  center: 'items-center py-14',
  bottom: 'items-end pt-10 pb-[7vh]',
  bottomDeep: 'items-end pt-10 pb-[4.5vh]',
  bottomLift: 'items-end pt-10 pb-[10vh]',
};

// Mobile section height, tuned per plate so the copy has quiet ground without crowding the subject.
const MOBILE_HEIGHT = {
  short: 'min-h-[max(66vh,520px)]',
  base: 'min-h-[max(72vh,570px)]',
  tall: 'min-h-[max(78vh,630px)]',
  xtall: 'min-h-[max(84vh,690px)]',
};

// Spanish copy runs longer than English. Give each mobile plate one extra height step so the
// approved composition (subject protected, copy in the negative space) is preserved.
const ES_HEIGHT_STEP = { short: 'base', base: 'tall', tall: 'xtall', xtall: 'xtall' };

// Measure is narrowed on plates where a focal detail sits to the right of the copy.
const MOBILE_MEASURE = {
  base: 'max-w-[20rem]',
  narrow: 'max-w-[16.5rem]',
};

// One section of the continuous vertical page. Not a card, not a slide.
const StorySection = ({ id: _id, anchor, eyebrow, headline, body, accent, slug, native, objectClass, mobileText = 'center', mobileScrim, mobileHeight = 'base', mobileMeasure = 'base', desktopText = 'left', desktopAlign = 'center', softScrim = false }) => (
  <section
    id={anchor}
    className={`relative px-6 lg:px-12 ${MOBILE_HEIGHT[mobileHeight]} lg:min-h-[520px] flex scroll-mt-24 ${MOBILE_ALIGN[mobileText]} ${desktopAlign === 'top' ? 'lg:items-start lg:pt-20 lg:pb-24' : 'lg:items-center lg:py-24'}`}
    data-testid={`landing-section-${eyebrow.toLowerCase().replace(/[^a-z]+/g, '-')}`}
  >
    <ScenicLayer
      slug={slug}
      native={native}
      objectClass={objectClass}
      mobileText={mobileText}
      mobileScrim={mobileScrim}
      right={desktopText === 'right'}
      soft={softScrim}
    />

    <div className="relative z-10 w-full max-w-[1500px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 22 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
        className={`${MOBILE_MEASURE[mobileMeasure]} lg:max-w-[34rem] ${desktopText === 'right' ? 'lg:ml-auto' : ''}`}
      >
        <span
          className="block text-[10px] lg:text-[11px] uppercase tracking-[0.3em] lg:tracking-[0.34em] text-[#D4A843]/85 font-medium"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {eyebrow}
        </span>

        <h2
          className="mt-4 lg:mt-6 text-[1.42rem] sm:text-3xl lg:text-[2.7rem] leading-[1.3] lg:leading-[1.18] tracking-[-0.015em] text-white/95 text-balance"
          style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
        >
          {headline}
        </h2>

        <p
          className="mt-5 lg:mt-7 text-[0.8rem] sm:text-sm md:text-base text-white/65 leading-[1.75] lg:leading-[1.8]"
          style={{ fontFamily: 'var(--font-display)' }}
        >
          {body}
        </p>

        {accent && (
          <p
            className="mt-5 lg:mt-7 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] lg:tracking-[0.22em] leading-[1.9] lg:leading-[2] text-[#D4A843]/90"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {accent}
          </p>
        )}
      </motion.div>
    </div>
  </section>
);

// ═══════════════════════════════════════════════════════════════════════════
// PLANS — the smallest honest answer to "what does this cost and what do I get?"
// Nobody should have to create an account to find that out. The numbers are read live
// from /api/subscription/plans, which reads the same configuration the gates enforce, so a
// figure shown here can never drift from the figure a member is held to. Nothing is called
// unlimited, and no capability without an approved allowance appears at all.
// ═══════════════════════════════════════════════════════════════════════════
const PLAN_GRID = 'grid grid-cols-[minmax(0,1.28fr)_minmax(0,1fr)_minmax(0,1.12fr)] items-stretch';

const PlanRow = ({ label, free, premium, testid }) => (
  <div className={`${PLAN_GRID} border-t border-[#F4EFE7]/[0.07]`} data-testid={testid}>
    <div className="flex items-center px-3 sm:px-5 py-4 text-[13px] sm:text-sm leading-snug text-[#F4EFE7]">
      {label}
    </div>
    <div className="flex items-center justify-center px-2 py-4 text-center text-[13px] sm:text-sm text-[#F4EFE7]/[0.74] tabular-nums">
      {free}
    </div>
    <div
      className="flex items-center justify-center px-2 py-4 text-center text-[13px] sm:text-sm text-[#E8C58F] tabular-nums bg-[#E8C58F]/[0.05]"
      style={{ boxShadow: 'inset 1px 0 0 rgba(232,197,143,0.3), inset -1px 0 0 rgba(232,197,143,0.3)' }}
    >
      {premium}
    </div>
  </div>
);

const PlansSection = ({ c, lang, onStart }) => {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription/plans`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (alive && data) setPlans(data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const n = (value) => new Intl.NumberFormat(lang === 'es' ? 'es-ES' : 'en-US').format(value);
  const rows = plans ? [
    { key: 'message', label: c.planRowMessage, free: n(plans.essential.message), premium: n(plans.premium.message) },
    { key: 'listen', label: c.planRowListen, free: n(plans.essential.listen_minutes), premium: n(plans.premium.listen_minutes) },
    { key: 'dreams', label: c.planRowDreams, free: n(plans.essential.dreams), premium: n(plans.premium.dreams) },
    { key: 'talk', label: c.planRowTalk, free: n(plans.essential.talk_minutes), premium: n(plans.premium.talk_minutes) },
    { key: 'image', label: c.planRowImage, free: n(plans.essential.image_creation), premium: n(plans.premium.image_creation) },
    { key: 'audio', label: c.planRowAudio, free: n(plans.essential.shared_audio), premium: n(plans.premium.shared_audio) },
    { key: 'video', label: c.planRowVideo, free: n(plans.essential.video), premium: n(plans.premium.video) },
  ] : [];

  return (
    <section
      id="plans"
      className="relative z-10 px-6 lg:px-12 py-20 lg:py-28 scroll-mt-24"
      data-testid="landing-plans"
    >
      <div className="max-w-[860px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <p
            className="uppercase text-[10px] sm:text-[11px] tracking-[0.22em] text-[#D4A843]/85 font-medium"
            style={{ fontFamily: 'var(--font-display)' }}
            data-testid="landing-plans-eyebrow"
          >
            {c.plansEyebrow}
          </p>
          <h2
            className="mt-5 text-[1.6rem] sm:text-3xl lg:text-[2.6rem] leading-[1.3] tracking-[-0.015em] text-white/92 text-balance"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
            data-testid="landing-plans-headline"
          >
            {c.plansHeadline}
          </h2>
        </motion.div>

        {/* The same three-column grammar as the in-app Subscription screen: one vocabulary of
            words, no ticks and no crosses, the six approved differences in the open. */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 lg:mt-14 rounded-[18px] border border-[#F4EFE7]/10 bg-white/[0.028] overflow-hidden"
          data-testid="landing-plans-table"
        >
          <div className={PLAN_GRID}>
            <div className="flex items-center px-3 sm:px-5 py-5 text-[13px] sm:text-sm font-semibold text-[#F4EFE7]/90">
              {c.plansEachMonth}
            </div>
            <div className="flex flex-col items-center justify-center px-2 py-5 text-center" data-testid="landing-plan-free">
              <span
                className="text-[21px] sm:text-2xl leading-tight text-[#F4EFE7]"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
              >
                {c.planFreeName}
              </span>
              <span className="mt-1 text-[10.5px] sm:text-[11px] text-[#F4EFE7]/[0.42]" data-testid="landing-plan-free-price">
                {plans ? `$${plans.essential_price}${c.planPerMonth}` : ''}
              </span>
            </div>
            <div
              className="flex flex-col items-center justify-center px-2 py-5 text-center bg-[#E8C58F]/[0.05] rounded-t-[10px]"
              style={{ boxShadow: 'inset 1px 0 0 rgba(232,197,143,0.3), inset -1px 0 0 rgba(232,197,143,0.3), inset 0 1px 0 rgba(232,197,143,0.3)' }}
              data-testid="landing-plan-premium"
            >
              <span
                className="text-[21px] sm:text-2xl leading-tight text-[#E8C58F]"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}
              >
                {c.planPremiumName}
              </span>
              <span className="mt-1 text-[10.5px] sm:text-[11px] text-[#F4EFE7]/[0.52]" data-testid="landing-plan-premium-price">
                {plans ? `$${plans.monthly_price}${c.planPerMonth}` : ''}
              </span>
            </div>
          </div>

          {rows.map((row) => (
            <PlanRow
              key={row.key}
              label={row.label}
              free={row.free}
              premium={row.premium}
              testid={`landing-plan-row-${row.key}`}
            />
          ))}

          {plans && (
            <p
              className="m-0 px-5 pt-4 pb-5 border-t border-[#F4EFE7]/[0.07] text-xs leading-relaxed text-[#F4EFE7]/50"
              data-testid="landing-plans-footnote"
            >
              {c.plansFootnote(plans)}
            </p>
          )}
        </motion.div>

        <div className="mt-12 text-center">
          <button
            onClick={onStart}
            className="group inline-flex items-center gap-3.5 pl-8 pr-7 py-4 rounded-full text-[#0A0910] font-semibold tracking-wide shadow-[0_18px_45px_-18px_rgba(212,168,67,0.65)] hover:shadow-[0_22px_55px_-16px_rgba(212,168,67,0.8)] transition-shadow"
            style={{ background: 'linear-gradient(100deg, #EBCE86 0%, #D4A843 48%, #C08E22 100%)' }}
            data-testid="landing-plans-cta-btn"
          >
            <span className="text-[1.02rem]">{c.cta}</span>
            <ArrowRight size={19} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
          </button>
          {plans && (
            <p className="mt-5 text-white/45 text-sm" data-testid="landing-plans-trial-note">
              {c.plansTrialNote(plans.trial_days)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
};

// Main Landing Page Component
const LANDING_COPY = {
  en: {
    navLabels: { Home: 'Home', Features: 'Features', Pricing: 'Pricing', Privacy: 'Privacy', About: 'About' },
    signIn: 'Sign In',
    returnTop: 'Return to Top',
    privacy: 'Privacy',
    terms: 'Terms',
    cookies: 'Cookies',
    copyright_link: 'Copyright',
    heroEyebrow: 'A CALMER PLACE TO BE YOU',
    heroHeadlineLead: 'Everyone deserves to be ',
    heroHeadlineAccent: 'heard and understood.',
    heroBody: 'Meet Arvexis — a private AI companion that gets to know you over time, so you don’t have to keep starting over.',
    heroSupport: 'Talk, message, share what’s on your mind, explore a dream, or just be yourself.',
    cta: 'Meet Arvexis',
    whyEyebrow: 'WHY ARVEXIS',
    whyHeadline: 'Because being known should feel different from simply being answered.',
    closingLead: 'AI can know a lot about the world.',
    closingAccent: 'Arvexis is built to understand more about yours.',
    plansEyebrow: 'PLANS',
    plansHeadline: 'Meet her free for 7 days. Stay if she becomes part of your week.',
    planFreeName: 'Essential',
    planFreeLine: 'The real Arvexis, with Message and Listen at the centre.',
    planPremiumName: 'Premium',
    planPremiumLine: 'Twice the room every month, plus Step Into The Next Room.',
    planPerMonth: '/month',
    planPerYear: 'per year',
    plansEachMonth: 'Each month',
    planRowMessage: 'Messages',
    planRowListen: 'Listen, in minutes',
    planRowDreams: 'Dreams with Reflection',
    planRowTalk: 'Live Talk, in minutes',
    planRowImage: 'Images she creates',
    planRowAudio: 'Audio recordings she listens to',
    planRowResearch: 'Live look-ups on the web',
    planRowVideo: 'Videos you upload for her to watch',
    plansFootnote: (p) => `Each audio recording can be up to ${Math.round(p.shared_audio_max_seconds / 60)} minutes long. Allowances reset every month, and Arvexis always tells you where you stand.`,
    plansTrialNote: (days) => `New accounts start with Meet ARVEXIS: ${days} days free, once, no card, nothing renews. Essential and Premium renew monthly until you cancel.`,
    copyright: '© 2026 Arvexis. All rights reserved.',
  },
  es: {
    navLabels: { Home: 'Inicio', Features: 'Funciones', Pricing: 'Precios', Privacy: 'Privacidad', About: 'Acerca de' },
    signIn: 'Iniciar sesión',
    returnTop: 'Volver arriba',
    privacy: 'Privacidad',
    terms: 'Términos',
    cookies: 'Cookies',
    copyright_link: 'Derechos de Autor',
    heroEyebrow: 'UN LUGAR MÁS TRANQUILO PARA SER QUIEN ERES',
    heroHeadlineLead: 'Todos merecemos sentir que ',
    heroHeadlineAccent: 'nos escuchan y nos comprenden.',
    heroBody: 'Conoce a Arvexis — una compañera de IA privada que llega a conocerte con el tiempo, para que no tengas que empezar de cero una y otra vez.',
    heroSupport: 'Habla, escribe, comparte lo que tienes en mente, explora un sueño o simplemente sé tú.',
    cta: 'Conoce a Arvexis',
    whyEyebrow: 'POR QUÉ ARVEXIS',
    whyHeadline: 'Porque sentir que alguien realmente te conoce debería ser diferente a simplemente recibir una respuesta.',
    closingLead: 'AI puede saber mucho sobre el mundo.',
    closingAccent: 'Pero Arvexis está hecha para comprender mejor el tuyo.',
    plansEyebrow: 'PLANES',
    plansHeadline: 'Conócela gratis durante 7 días. Quédate si se vuelve parte de tu semana.',
    planFreeName: 'Essential',
    planFreeLine: 'La verdadera Arvexis, con Message y Escuchar en el centro.',
    planPremiumName: 'Premium',
    planPremiumLine: 'El doble de espacio cada mes y Pasar a la siguiente sala.',
    planPerMonth: '/mes',
    planPerYear: 'al año',
    plansEachMonth: 'Cada mes',
    planRowMessage: 'Mensajes',
    planRowListen: 'Escuchar, en minutos',
    planRowDreams: 'Sueños con Reflexión',
    planRowTalk: 'Talk en vivo, en minutos',
    planRowImage: 'Imágenes que ella crea',
    planRowAudio: 'Grabaciones de audio que ella escucha',
    planRowResearch: 'Búsquedas en vivo en la web',
    planRowVideo: 'Videos que subes para que ella los vea',
    plansFootnote: (p) => `Cada grabación de audio puede durar hasta ${Math.round(p.shared_audio_max_seconds / 60)} minutos. Las cantidades se renuevan cada mes, y Arvexis siempre te dice cómo vas.`,
    plansTrialNote: (days) => `Las cuentas nuevas empiezan con Meet ARVEXIS: ${days} días gratis, una sola vez, sin tarjeta y sin renovación. Essential y Premium se renuevan cada mes hasta que canceles.`,
    copyright: '© 2026 Arvexis. Todos los derechos reservados.',
  },
};

const LandingPage = ({ lang = 'en', setLang }) => {
  const c = LANDING_COPY[lang] || LANDING_COPY.en;
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = 'smooth';
    return () => { root.style.scrollBehavior = previous; };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-[#07070E] overflow-x-hidden" data-testid="landing-page">
      {/* Base surface below the Hero — solid premium dark, no legacy scenic raster */}
      <div className="fixed inset-0 z-0 bg-[#07070E]">
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(24,22,44,0.85) 0%, rgba(9,9,18,0.9) 45%, #07070E 100%)' }}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          HERO — DESKTOP COMPOSITION (Founder-approved, Landing V1)
          Background: supplied production asset, used exactly as delivered.
          Arvexis: canonical ArvexisPresence, separate live layer, never baked in.
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        id="top"
        className="relative z-10 flex flex-col overflow-hidden min-h-[100svh] dsk:h-[74vh] dsk:min-h-[600px] dsk:max-h-[900px]"
        data-testid="landing-hero"
      >
        {/* Production background — supplied asset unchanged as source-of-truth fallback;
            AVIF/WebP derivatives are performance-only re-encodes of the same pixels. */}
        <div className="absolute inset-0 -z-10">
          <picture className="block w-full h-full">
            <source
              media="(max-width: 959px)"
              type="image/avif"
              srcSet="/landing/v1/m-hero-640.avif 640w, /landing/v1/m-hero-941.avif 941w"
              sizes="100vw"
            />
            <source
              media="(max-width: 959px)"
              type="image/webp"
              srcSet="/landing/v1/m-hero-640.webp 640w, /landing/v1/m-hero-941.webp 941w"
              sizes="100vw"
            />
            <source
              type="image/avif"
              srcSet="/landing/hero-desktop-miami-1024.avif 1024w, /landing/hero-desktop-miami-1440.avif 1440w, /landing/hero-desktop-miami-1672.avif 1672w"
              sizes="100vw"
            />
            <source
              type="image/webp"
              srcSet="/landing/hero-desktop-miami-1024.webp 1024w, /landing/hero-desktop-miami-1440.webp 1440w, /landing/hero-desktop-miami-1672.webp 1672w"
              sizes="100vw"
            />
            <img
              src="/landing/hero-desktop-miami.png"
              alt=""
                className="w-full h-full object-cover object-center dsk:object-[50%_42%]"
              loading="eager"
              data-testid="landing-hero-background"
            />
          </picture>
          <div
            className="absolute inset-0 hidden dsk:block"
            style={{ background: 'linear-gradient(100deg, rgba(6,8,18,0.90) 0%, rgba(6,8,18,0.74) 24%, rgba(6,8,18,0.34) 50%, rgba(6,8,18,0.06) 72%, rgba(6,8,18,0.20) 100%)' }}
          />
          <div
            className="absolute inset-0 dsk:hidden"
            style={{ background: 'linear-gradient(to bottom, rgba(6,8,18,0.62) 0%, rgba(6,8,18,0.34) 20%, rgba(6,8,18,0.38) 38%, rgba(6,8,18,0.80) 54%, rgba(6,8,18,0.90) 100%)' }}
          />
          <div
            className="absolute inset-x-0 top-0 h-44"
            style={{ background: 'linear-gradient(to bottom, rgba(6,8,18,0.72), transparent)' }}
          />
          <div
            className="absolute inset-x-0 bottom-0 h-72"
            style={{ background: 'linear-gradient(to top, rgba(6,8,18,0.94) 0%, rgba(6,8,18,0.58) 42%, transparent 100%)' }}
          />
        </div>

        {/* ─── Header / navigation ─── */}
        <motion.header
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-20 px-6 dsk:px-12 pt-6 dsk:pt-7"
        >
          <div className="max-w-[1500px] mx-auto flex items-center justify-between gap-8">
            <a href="/" className="flex items-center gap-3 shrink-0" data-testid="landing-logo-home">
              <ArvexisLogo size={44} />
              <span
                className="hidden sm:block text-white/95 text-[1.6rem] leading-none tracking-[0.01em]"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
              >
                Arvexis
              </span>
            </a>

            <DesktopNav navLabels={c.navLabels} />

            <div className="flex items-center gap-5">
              <LanguageControl lang={lang} setLang={setLang} />
              <button
                onClick={() => navigate('/login')}
                className="px-6 py-2.5 text-sm font-medium text-white/90 border border-white/25 rounded-full backdrop-blur-md bg-white/[0.04] hover:border-[#D4A843]/60 hover:text-white transition-colors"
                data-testid="landing-signin-btn"
              >
                {c.signIn}
              </button>
              <MobileNav
                isOpen={mobileMenuOpen}
                setIsOpen={setMobileMenuOpen}
                navigate={navigate}
                lang={lang}
                setLang={setLang}
                copy={c}
              />
            </div>
          </div>
        </motion.header>

        {/* ─── Hero body: left typography · center-right Arvexis ─── */}
        <div className="relative z-10 flex-1 flex items-start dsk:items-center px-6 dsk:px-12 pt-1 pb-6 dsk:py-0">
          <div className="w-full max-w-[1500px] mx-auto grid dsk:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)] gap-3 dsk:gap-8 items-center">

            {/* Left — typography area */}
            <div className="max-w-[42rem] dsk:pl-2 order-2 dsk:order-none">
              <motion.span
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
                className={`block mb-5 uppercase text-[#D4A843]/85 font-medium ${lang === 'es' ? 'text-[9.5px] tracking-[0.18em] lg:text-[11px] lg:tracking-[0.3em]' : 'text-[11px] tracking-[0.36em]'}`}
                style={{ fontFamily: 'var(--font-display)' }}
                data-testid="landing-hero-eyebrow"
              >
                {c.heroEyebrow}
              </motion.span>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.25, ease: [0.16, 1, 0.3, 1] }}
                className="text-4xl sm:text-5xl lg:text-[4.15rem] leading-[1.06] tracking-[-0.015em] text-white/95"
                style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
                data-testid="landing-hero-headline"
              >
                {c.heroHeadlineLead}
                <span className="text-[#D4A843] italic">{c.heroHeadlineAccent}</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 text-base md:text-lg text-white/70 leading-[1.75] text-balance"
                style={{ fontFamily: 'var(--font-display)' }}
                data-testid="landing-hero-subhead"
              >
                {c.heroBody}
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.58, ease: [0.16, 1, 0.3, 1] }}
                className="mt-5 text-sm md:text-base text-white/50 leading-[1.75] text-balance"
                style={{ fontFamily: 'var(--font-display)' }}
                data-testid="landing-hero-support"
              >
                {c.heroSupport}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.72, ease: [0.16, 1, 0.3, 1] }}
                className="mt-11"
              >
                <button
                  onClick={() => navigate('/register')}
                  className="group inline-flex items-center gap-3.5 pl-8 pr-7 py-4 rounded-full text-[#0A0910] font-semibold tracking-wide shadow-[0_18px_45px_-18px_rgba(212,168,67,0.65)] hover:shadow-[0_22px_55px_-16px_rgba(212,168,67,0.8)] transition-shadow"
                  style={{ background: 'linear-gradient(100deg, #EBCE86 0%, #D4A843 48%, #C08E22 100%)' }}
                  data-testid="landing-meet-arvexis-btn"
                >
                  <span className="text-[1.02rem]">{c.cta}</span>
                  <ArrowRight size={19} strokeWidth={2} className="transition-transform group-hover:translate-x-1" />
                </button>
              </motion.div>
            </div>

            {/* Right — canonical Arvexis presence. Her own luminosity only: no floor
                ellipse, pedestal, platform, halo or presentation object. */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.4, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="relative flex justify-center dsk:justify-end dsk:pr-[9%] order-1 dsk:order-none"
              data-testid="arvexis-presence-container"
            >
              <ArvexisPresence size="large" className="-my-1 dsk:my-0" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          CAPABILITY ORIENTATION — directly beneath the Hero (Image 1)
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        id="features"
        className="relative z-10 px-6 lg:px-12 py-12 lg:py-20 scroll-mt-24"
        style={{
          background:
            'linear-gradient(to bottom, rgba(10,15,34,0) 0%, #0A0F22 14%, #0A0F22 74%, rgba(10,15,34,0) 100%)',
        }}
        data-testid="landing-capability-strip"
      >
        <div className="max-w-[1500px] mx-auto grid grid-cols-6 lg:grid-cols-5 gap-y-10 lg:gap-y-0 lg:divide-x lg:divide-white/[0.10]">
            {CAPABILITIES.map((capability, i) => (
            <CapabilityColumn
              key={capability.title}
              {...capability}
              {...(lang === 'es' && capability.es ? capability.es : {})}
              span={i < 2 ? 'col-span-3 lg:col-span-1' : 'col-span-2 lg:col-span-1'}
            />
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          WHY ARVEXIS — transition
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        id="experience"
        className="relative px-6 lg:px-12 py-16 lg:py-28 min-h-[40vh] lg:min-h-[360px] flex items-center scroll-mt-24"
        data-testid="landing-why-arvexis"
      >
        <ScenicLayer slug="why-transition" native={2000} center mobileText="center" />

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-120px' }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative z-10 w-full max-w-3xl mx-auto text-center"
        >
          <span
            className="block text-[10px] lg:text-[11px] uppercase tracking-[0.32em] lg:tracking-[0.36em] text-[#D4A843]/85 font-medium"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {c.whyEyebrow}
          </span>
          <h2
            className="mt-5 lg:mt-7 text-[1.5rem] sm:text-3xl lg:text-[2.9rem] leading-[1.36] lg:leading-[1.26] tracking-[-0.015em] text-white/95 text-balance max-w-[20rem] mx-auto lg:max-w-none"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
            data-testid="landing-why-arvexis-headline"
          >
            {c.whyHeadline}
          </h2>
        </motion.div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════
          THE STORY — one continuous vertical journey
          ═══════════════════════════════════════════════════════════════════ */}
      {STORY_SECTIONS.map((section) => (
        <StorySection
          key={section.id}
          {...section}
          {...(lang === 'es' && section.es ? section.es : {})}
          mobileHeight={lang === 'es' ? ES_HEIGHT_STEP[section.mobileHeight || 'base'] : section.mobileHeight}
        />
      ))}

      {/* ═══════════════════════════════════════════════════════════════════
          PLANS — Free and Premium, visible without an account
          ═══════════════════════════════════════════════════════════════════ */}
      <PlansSection c={c} lang={lang} onStart={() => navigate('/register')} />

      {/* ═══════════════════════════════════════════════════════════════════
          FINAL DAWN / CLOSING (verbatim) + MEET ARVEXIS
          ═══════════════════════════════════════════════════════════════════ */}
      <section
        className={`relative px-6 lg:px-12 pb-16 lg:pt-[12vh] lg:pb-32 min-h-[72vh] lg:min-h-[600px] flex items-start lg:items-start ${lang === 'es' ? 'pt-[3vh]' : 'pt-[8vh]'}`}
        data-testid="landing-closing"
      >
        <ScenicLayer slug="final-dawn" native={1916} center mobileText="upper" />
        <div className="relative z-10 max-w-5xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-120px' }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="text-[1.5rem] sm:text-3xl lg:text-[3rem] leading-[1.34] lg:leading-[1.24] tracking-[-0.015em] text-white/92 text-balance"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 300 }}
            data-testid="landing-closing-thesis"
          >
            <span className="block">{c.closingLead}</span>
            <span className="block mt-4 lg:mt-6 text-[#D4A843] italic">
              {c.closingAccent}
            </span>
          </motion.h2>
        </div>
      </section>
      {/* ═══════════════════════════════════════════════════════════════════
          FOOTER (Simplified)
          ═══════════════════════════════════════════════════════════════════ */}
      {/* The footer's own Return to Top has been replaced by the shared floating
          Back to Top control mounted at the end of this page. */}

      <footer className="relative z-10 px-6 py-10 border-t border-white/5" data-testid="landing-footer">
        <div className="max-w-[1500px] mx-auto flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-white/40 text-sm">
            <a href="/privacy" className="hover:text-white/60 transition-colors" data-testid="landing-footer-privacy-link">{c.privacy}</a>
            <a href="/terms" className="hover:text-white/60 transition-colors" data-testid="landing-footer-terms-link">{c.terms}</a>
            <a href="/cookies" className="hover:text-white/60 transition-colors" data-testid="landing-footer-cookies-link">{c.cookies}</a>
            <a href="/copyright" className="hover:text-white/60 transition-colors" data-testid="landing-footer-copyright-link">{c.copyright_link}</a>
          </div>
          <div
            className="flex items-center gap-6 text-white/40 text-sm select-none"
            data-testid="landing-footer-social"
          >
            {[
              { label: 'YouTube', href: 'https://youtube.com/@arvexisapp?si=cFNlC50b-DwU3UrC' },
              { label: 'Instagram', href: 'https://www.instagram.com/arvexis_app' },
              { label: 'LinkedIn', href: 'https://www.linkedin.com/company/arvexis/' },
            ].map(({ label, href }) => (
              href ? (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white/60 transition-colors"
                  data-testid={`landing-footer-${label.toLowerCase()}`}
                >
                  {label}
                </a>
              ) : (
                <span key={label} data-testid={`landing-footer-${label.toLowerCase()}`}>{label}</span>
              )
            ))}
          </div>
          <span className="text-white/30 text-sm">{c.copyright}</span>
        </div>
      </footer>
      <BackToTop lang={lang} />
    </div>
  );
};

export default LandingPage;
